import React, { useEffect, useState } from "react";
import { Wind, CloudRain, Thermometer, RefreshCw, Sun, Cloud, CloudFog, CloudDrizzle, CloudSnow, CloudLightning, CloudSun } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Monte Argentario (Porto Ercole / Porto Santo Stefano) approx coordinates
export const LAT = 42.395;
export const LON = 11.165;
const URL = `${process.env.REACT_APP_BACKEND_URL}/api/weather?latitude=${LAT}&longitude=${LON}&current=temperature_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum&forecast_days=4&timezone=Europe%2FRome`;

// WMO weather code → { iconKey, labelKey }
export const codeMap = (code) => {
    if (code === 0) return { Icon: Sun, key: "clear" };
    if (code === 1) return { Icon: CloudSun, key: "mainlyClear" };
    if (code === 2) return { Icon: CloudSun, key: "partlyCloudy" };
    if (code === 3) return { Icon: Cloud, key: "overcast" };
    if (code === 45 || code === 48) return { Icon: CloudFog, key: "fog" };
    if ([51, 53, 55, 56, 57].includes(code)) return { Icon: CloudDrizzle, key: "drizzle" };
    if ([61, 63, 66].includes(code)) return { Icon: CloudRain, key: "rain" };
    if ([65, 67, 82].includes(code)) return { Icon: CloudRain, key: "heavyRain" };
    if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, key: "snow" };
    if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, key: "thunderstorm" };
    if ([80, 81].includes(code)) return { Icon: CloudRain, key: "showers" };
    return { Icon: Cloud, key: "unknown" };
};

/* Race-day label: uncertain drizzle (<30% · <1 mm) reads "Variable · N% a few drops" instead of "Drizzle". */
export const LOW_RAIN_PROB = 30;
export const raceCondition = (code, prob, rain) => {
    const lowRain = code >= 51 && prob != null && prob < LOW_RAIN_PROB && (rain == null || rain < 1);
    return lowRain ? { Icon: CloudSun, key: "variable", lowRain: true } : { ...codeMap(code), lowRain: false };
};

export default function WeatherWidget() {
    const { t, lang } = useLang();
    const [data, setData] = useState(null);
    const [daily, setDaily] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true); setError(false);
        try {
            const res = await fetch(URL, { cache: "no-store" });
            const json = await res.json();
            if (!json?.current) throw new Error("no data");
            setData(json.current);
            setDaily(json.daily || null);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updatedLabel = (() => {
        if (!data?.time) return "";
        try {
            const d = new Date(data.time);
            return d.toLocaleTimeString(lang === "en" ? "en-GB" : "it-IT", { hour: "2-digit", minute: "2-digit" });
        } catch { return ""; }
    })();

    const { Icon, key } = data ? codeMap(data.weather_code) : { Icon: Cloud, key: "unknown" };

    return (
        <section data-testid="weather-widget" className="mt-12 border border-white/10 bg-abnormal-charcoal/60 backdrop-blur-sm">
            <div className="flex flex-col md:flex-row md:items-stretch">
                {/* Left: hero */}
                <div className="flex-1 p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10 flex items-center gap-6">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-abnormal-acid/40 bg-abnormal-acid/10 flex items-center justify-center text-abnormal-acid shrink-0">
                        <Icon size={44} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold">{t("weather.eyebrow")}</p>
                        <p className="font-display text-3xl md:text-4xl uppercase leading-none mt-2">{t("weather.title")}</p>
                        {loading ? (
                            <p className="text-sm text-zinc-400 mt-2 uppercase tracking-wider">{t("weather.loading")}</p>
                        ) : error || !data ? (
                            <p className="text-sm text-zinc-400 mt-2 uppercase tracking-wider">{t("weather.error")}</p>
                        ) : (
                            <p className="text-sm text-zinc-300 mt-2">
                                <span className="font-display text-5xl text-white align-middle">{Math.round(data.temperature_2m)}°</span>
                                <span className="ml-3 uppercase tracking-wider text-zinc-300 align-middle">{t(`weather.codes.${key}`)}</span>
                            </p>
                        )}
                    </div>
                </div>

                {/* Right: stats */}
                <div className="grid grid-cols-3 md:w-[440px]">
                    <Stat label={t("weather.feels")} value={data ? `${Math.round(data.apparent_temperature)}°` : "—"} Icon={Thermometer} />
                    <Stat label={t("weather.wind")} value={data ? `${Math.round(data.wind_speed_10m)} km/h` : "—"} Icon={Wind} />
                    <Stat label={t("weather.rain")} value={data ? `${(data.precipitation ?? 0).toFixed(1)} mm` : "—"} Icon={CloudRain} />
                </div>
            </div>
            <div className="px-5 md:px-8 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center justify-between border-t border-white/5">
                <span>{updatedLabel ? `${t("weather.updated")} ${updatedLabel}` : ""}</span>
                <button
                    type="button"
                    onClick={load}
                    data-testid="weather-refresh-btn"
                    className="inline-flex items-center gap-2 hover:text-abnormal-acid transition-colors"
                    aria-label="refresh"
                >
                    <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> {t("weather.source")}
                </button>
            </div>

            {/* 3-day forecast */}
            {daily && Array.isArray(daily.time) && daily.time.length >= 4 && (
                <div className="border-t border-white/10 px-5 md:px-8 py-5 md:py-6">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">{t("weather.forecastTitle")}</p>
                    <div className="grid grid-cols-3 gap-3" data-testid="weather-forecast">
                        {[1, 2, 3].map((idx) => {
                            const day = daily.time[idx];
                            const c = codeMap(daily.weather_code[idx]);
                            const DayIcon = c.Icon;
                            const dayLabel = (() => {
                                try {
                                    const d = new Date(day);
                                    if (idx === 1) return t("weather.tomorrow");
                                    return d.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { weekday: "short" });
                                } catch { return ""; }
                            })();
                            return (
                                <div key={day} data-testid={`weather-day-${idx}`} className="border border-white/10 bg-black/40 p-3 md:p-4 flex flex-col">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">{dayLabel}</span>
                                        <DayIcon size={18} className="text-abnormal-acid" />
                                    </div>
                                    <p className="text-xs text-zinc-300 mt-2 uppercase tracking-wider line-clamp-1">{t(`weather.codes.${c.key}`)}</p>
                                    <div className="flex items-baseline gap-2 mt-2">
                                        <span className="font-display text-2xl md:text-3xl text-white">{Math.round(daily.temperature_2m_max[idx])}°</span>
                                        <span className="font-display text-base text-zinc-500">/ {Math.round(daily.temperature_2m_min[idx])}°</span>
                                    </div>
                                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">
                                        {t("weather.precip")} {(daily.precipitation_sum[idx] ?? 0).toFixed(1)} mm
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
}

function Stat({ label, value, Icon }) {
    return (
        <div className="p-4 md:p-5 border-r last:border-r-0 border-white/10 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-zinc-500">
                <Icon size={14} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold">{label}</span>
            </div>
            <p className="font-display text-2xl md:text-3xl uppercase mt-2">{value}</p>
        </div>
    );
}
